// ===================================
// API SERVICE - AI CONFIGURATION
// src/services/aiConfigApi.ts
// ===================================

/**
 * AI Configuration API Service
 *
 * Provides API methods for:
 * - Fetching available providers and modes
 * - Managing business configuration
 * - Managing user preferences
 * - Testing provider connections
 *
 * Usage:
 *   import { aiConfigApi } from '@/services/aiConfigApi';
 *   const providers = await aiConfigApi.getProviders();
 */

import {
  AIProvider,
  AIMode,
  BusinessAIConfig,
  UserAIPreferences,
  EffectiveAIConfig,
  AIProviderTestResult,
  DocumentDetailedStatus,
  Document,
  Message,
  SourceDocument,
  SearchResult,
} from "../types";

import api from "./api"; // Your existing axios instance

// ===================================
// SERVICE-SPECIFIC TYPES
// ===================================

/** Response shape for providers endpoint */
export interface ProvidersResponse {
  llm_providers: AIProvider[];
  embedding_providers: AIProvider[];
  modes: AIMode[];
  registered: { llm: string[]; embedding: string[] };
}

/** Response shape for user preferences endpoint */
export interface UserPreferencesResponse {
  preferences: UserAIPreferences;
  business_allows_override: boolean;
  business_allows_api_keys: boolean;
  has_custom_api_key: boolean;
}

/** Extended chat message options with AI configuration */
export interface ChatMessageOptions {
  message: string;
  mode?: string;
  llm_provider?: string;
  llm_model?: string;
  embedding_provider?: string;
  temperature?: number;
  use_rag?: boolean;
  include_citations?: boolean;
}

/** Chat message response with AI metadata */
export interface ChatMessageResponse {
  message: {
    id: string;
    role: "assistant";
    content: string;
  };
  context_used: boolean;
  sources?: Array<{
    id: string;
    title: string;
    similarity: number;
  }>;
  metadata: {
    mode: string;
    mode_display: string;
    llm_provider: string;
    llm_model: string;
    used_fallback: boolean;
    rag_enabled: boolean;
    agentic_enabled: boolean;
    processing_time: number;
  };
}

/** Pre-flight check response */
export interface PreFlightCheckResponse {
  canSearch: boolean;
  reason?: string;
  stats: {
    total_chunks: number;
    chunks_with_embeddings: number;
    total_documents: number;
  };
}

/** Document processing trigger response */
export interface ProcessingTriggerResponse {
  message: string;
  document: Document;
  job?: {
    id: string;
    status: string;
  };
}

// ===================================
// AI CONFIGURATION API
// ===================================

export const aiConfigApi = {
  /**
   * Get all available providers and modes
   */
  getProviders: () => api.get<ProvidersResponse>("/ai-config/providers"),

  /**
   * Get business AI configuration
   */
  getBusinessConfig: () =>
    api.get<{ config: BusinessAIConfig }>("/ai-config/business"),

  /**
   * Update business AI configuration (admin only)
   */
  updateBusinessConfig: (data: Partial<BusinessAIConfig>) =>
    api.put<{ config: BusinessAIConfig; message: string }>(
      "/ai-config/business",
      data
    ),

  /**
   * Get user AI preferences
   */
  getUserPreferences: () =>
    api.get<UserPreferencesResponse>("/ai-config/user"),

  /**
   * Update user AI preferences
   */
  updateUserPreferences: (data: {
    preferred_llm_provider?: string;
    preferred_llm_model?: string;
    preferred_mode?: string;
    openai_api_key?: string;
  }) =>
    api.put<{
      preferences: UserAIPreferences;
      message: string;
      has_custom_api_key: boolean;
    }>("/ai-config/user", data),

  /**
   * Get effective configuration (combines business defaults + user overrides)
   */
  getEffectiveConfig: () =>
    api.get<{ config: EffectiveAIConfig }>("/ai-config/effective"),

  /**
   * Test LLM provider connection
   */
  testLLM: (data: { provider: string; model?: string; api_key?: string }) =>
    api.post<AIProviderTestResult>("/ai-config/test-llm", data),

  /**
   * Remove user's custom API key
   */
  removeApiKey: () => api.delete<{ message: string }>("/ai-config/user/api-key"),

  /**
   * Get available modes (convenience method)
   */
  getModes: async (): Promise<AIMode[]> => {
    const response = await api.get<ProvidersResponse>("/ai-config/providers");
    return response.data.modes;
  },
};

// ===================================
// DOCUMENT API EXTENSIONS
// ===================================

export const documentApiExtensions = {
  /**
   * Get detailed document status
   */
  getDetailedStatus: (documentId: string) =>
    api.get<DocumentDetailedStatus>(`/documents/${documentId}/status`),

  /**
   * Manually trigger text extraction
   */
  triggerExtraction: (documentId: string, options?: { force?: boolean }) =>
    api.post<ProcessingTriggerResponse>(
      `/documents/${documentId}/extract`,
      options
    ),

  /**
   * Manually trigger embedding generation
   */
  triggerEmbedding: (documentId: string, options?: { force?: boolean }) =>
    api.post<ProcessingTriggerResponse>(
      `/documents/${documentId}/embed`,
      options
    ),

  /**
   * Get RAG-ready documents only
   */
  getRAGReadyDocuments: (params?: { limit?: number; offset?: number }) =>
    api.get<{ documents: Document[]; total: number }>("/documents/", {
      params: { ...params, rag_ready: true },
    }),

  /**
   * Get documents by status
   */
  getByStatus: (status: {
    processing_status?: string;
    extraction_status?: string;
    embedding_status?: string;
  }) => api.get<{ documents: Document[] }>("/documents/", { params: status }),
};

// ===================================
// CHAT API EXTENSIONS
// ===================================

export const chatApiExtensions = {
  /**
   * Send message with AI configuration overrides
   */
  sendMessageWithConfig: (sessionId: string, options: ChatMessageOptions) =>
    api.post<ChatMessageResponse>(
      `/chat/sessions/${sessionId}/messages`,
      options
    ),

  /**
   * Get available modes for chat
   */
  getModes: () => api.get<{ modes: AIMode[] }>("/chat/modes"),

  /**
   * Update session default settings
   */
  updateSessionSettings: (
    sessionId: string,
    settings: {
      default_mode?: string;
      default_llm_provider?: string;
      default_llm_model?: string;
    }
  ) => api.patch(`/chat/sessions/${sessionId}/settings`, settings),
};

// ===================================
// SEARCH API EXTENSIONS
// ===================================

export const searchApiExtensions = {
  /**
   * Semantic search with provider specification
   */
  similarWithProvider: (
    query: string,
    options?: {
      limit?: number;
      similarity_threshold?: number;
      document_ids?: string[];
      file_types?: string[];
      embedding_provider?: string;
    }
  ) => api.post<{ results: SearchResult[] }>("/search/similar", { query, ...options }),

  /**
   * RAG query with AI config overrides
   */
  ragWithConfig: (
    query: string,
    options?: {
      search_type?: string;
      limit?: number;
      temperature?: number;
      max_tokens?: number;
      include_citations?: boolean;
      mode?: string;
      llm_provider?: string;
      llm_model?: string;
    }
  ) =>
    api.post<{
      response: string;
      sources: SourceDocument[];
      metadata: Record<string, any>;
    }>("/search/rag", { query, ...options }),

  /**
   * Get pre-flight check status (can we search?)
   */
  preFlightCheck: () => api.get<PreFlightCheckResponse>("/search/preflight"),
};

// ===================================
// HEALTH API EXTENSIONS
// ===================================

export const healthApiExtensions = {
  /**
   * Get detailed system health including AI providers
   */
  getDetailedHealth: () => api.get("/health"),

  /**
   * Get RAG service health specifically
   */
  getRAGHealth: () => api.get("/test/rag"),

  /**
   * Get processing system health
   */
  getProcessingHealth: () => api.get("/test/processing"),
};

// ===================================
// HELPER FUNCTIONS
// ===================================

/**
 * Build message payload with AI configuration
 */
export function buildMessagePayload(
  message: string,
  config: Partial<ChatMessageOptions> = {}
): ChatMessageOptions {
  const payload: ChatMessageOptions = { message };

  if (config.mode) payload.mode = config.mode;
  if (config.llm_provider) payload.llm_provider = config.llm_provider;
  if (config.llm_model) payload.llm_model = config.llm_model;
  if (config.embedding_provider)
    payload.embedding_provider = config.embedding_provider;
  if (config.temperature !== undefined) payload.temperature = config.temperature;
  if (config.use_rag !== undefined) payload.use_rag = config.use_rag;
  if (config.include_citations !== undefined)
    payload.include_citations = config.include_citations;

  return payload;
}

/**
 * Parse AI metadata from response
 */
export function parseAIMetadata(response: ChatMessageResponse) {
  const { metadata } = response;

  const providerDisplayMap: Record<string, string> = {
    openai: "OpenAI",
    "claude-bedrock": "Claude (Bedrock)",
    anthropic: "Anthropic",
    azure: "Azure OpenAI",
  };

  return {
    mode: metadata.mode,
    modeDisplay: metadata.mode_display,
    provider: metadata.llm_provider,
    model: metadata.llm_model,
    usedFallback: metadata.used_fallback,
    ragEnabled: metadata.rag_enabled,
    agenticEnabled: metadata.agentic_enabled,
    processingTime: metadata.processing_time,

    // Formatted display
    providerDisplay:
      providerDisplayMap[metadata.llm_provider] || metadata.llm_provider,
    timeDisplay: `${metadata.processing_time}ms`,
  };
}

/**
 * Get display name for a provider
 */
export function getProviderDisplayName(providerKey: string): string {
  const displayNames: Record<string, string> = {
    openai: "OpenAI",
    "claude-bedrock": "Claude (Bedrock)",
    anthropic: "Anthropic",
    azure: "Azure OpenAI",
    cohere: "Cohere",
    "huggingface": "Hugging Face",
  };
  return displayNames[providerKey] || providerKey;
}

/**
 * Get icon for a mode
 */
export function getModeIcon(modeKey: string): string {
  const icons: Record<string, string> = {
    chat: "💬",
    rag: "📚",
    agentic: "🤖",
    code: "💻",
    creative: "🎨",
  };
  return icons[modeKey] || "🔧";
}

// ===================================
// DEFAULT EXPORT
// ===================================

export default aiConfigApi;